import { Dialog } from '../../../../../../widgets/dialog';
import Stack from '../../../../../../widgets/stack/stack';
import React from 'react';
import { useRelayEnvironment } from 'react-relay';
import { fetchQuery, graphql } from 'relay-runtime';
import { merchantProduct_item$data } from 'src/components/pages/productConfiguration/state/queries/__generated__/merchantProduct_item.graphql';
import StoreCatalog from 'src/components/pages/productConfiguration/storeCatalog/storeCatalog';
import { T } from 'src/shared/helpers';
import theme from 'src/shared/theme';
import { RelayFragment } from 'src/shared/types';
import { useValidationContext, ValidationStateProvider } from 'src/shared/validation';
import Select from 'react-select';
import styled from 'styled-components';
import { ValidationBox } from '../../../../../../widgets/validation/validationBox';
import { ComposerAttributeNode } from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { ComposerAttributeNodeAdapter } from '../helpers/composerNodesAdapter';
import { getRetargetObjectsAndMaterials } from '../helpers/composerHelpers';
import { composerImportAttributeDialogAttributesQuery } from './__generated__/composerImportAttributeDialogAttributesQuery.graphql';
import { composerImportAttributeDialogModelIDQuery } from './__generated__/composerImportAttributeDialogModelIDQuery.graphql';

interface SceneObject {
    id: string;
    name: string;
}

interface SceneMaterial {
    id: string;
    name: string;
}

interface ObjectMatch {
    srcObject: SceneObject;
    dstObjects: SceneObject[];
}

interface MaterialMatch {
    srcMaterial: SceneMaterial;
    dstMaterial: SceneMaterial | null;
}

enum ComposerImportStep {
    SelectProduct = 0,
    SelectAttributes = 1,
    MatchObjectAndMaterials = 2
}

export interface ComposerImportAttributesOutput {
    selectedAttributes: ComposerAttributeNode[];
    objectMatches: Record<string, string>;
    materialMatches: Record<string, string>;
}

interface ComposerImportAttributesDialogProps {
    salesChannelID: string;
    materialsList: BABYLON.Material[];
    objectsList: BABYLON.AbstractMesh[];
    onConfirm: (attributes: ComposerImportAttributesOutput) => void;
}
// New styled components for the step system
const StepContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: 16px;
`;

const StepHeader = styled.div<{ $isActive: boolean; $isCompleted: boolean; $isDisabled: boolean }>`
    display: flex;
    padding: 12px 16px;
    background-color: ${({ $isActive, $isCompleted }) => ($isActive ? '#f0f0f0ff' : $isCompleted ? '#f0f0f0ff' : '#ffffff')};
    border: 1px solid ${theme.app.borderColor};
    border-radius: 4px;
    margin-bottom: 2px;
    cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'pointer')};
    opacity: ${({ $isDisabled }) => ($isDisabled ? theme.app.disabledOpacity : 1)};
`;

const StepNumber = styled.div<{ $isActive: boolean; $isCompleted: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background-color: ${({ $isActive, $isCompleted }) => ($isCompleted ? theme.app.secondary : $isActive ? theme.app.primary : '#9e9e9e')};
    color: white;
    margin-right: 12px;
    font-size: 14px;
    font-weight: bold;
`;

const StepTitle = styled.div`
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 16px;
    flex-grow: 1;
`;

const StepContent = styled.div<{ isOpen: boolean }>`
    padding: ${({ isOpen }) => (isOpen ? '16px' : '0')};
    max-height: ${({ isOpen }) => (isOpen ? '500px' : '0')};
    overflow: hidden;
    transition: all 0.3s ease;
    border: ${({ isOpen }) => (isOpen ? '1px solid #e0e0e0' : 'none')};
    border-top: none;
    border-radius: 0 0 4px 4px;
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
`;

const StepContentInner = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const ComposerImportAttributeList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 8px;
    margin: 8px 0;
`;

const ComposerImportAttributeItem = styled.div<{ $selected?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background-color: ${theme.app.backgroundColorBase};
    border: 1px solid ${theme.app.borderColor};
    text-align: left;
    cursor: pointer;

    &:hover {
        background-color: ${(props) => (props.$selected ? theme.app.secondary : theme.app.backgroundColor)};
    }

    ${(props) =>
        props.$selected &&
        `
		background-color: ${theme.app.secondary};
		color: ${theme.app.backgroundColor};
	`}
`;

const MatchFormInline = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: space-around;
    align-items: center;
    min-width: 300px;
    gap: 15px;
`;

const modelIDByCodeQuery = graphql`
    query composerImportAttributeDialogModelIDQuery($code: String!, $salesChannelId: String!) {
        modelIDByCode(code: $code, salesChannelId: $salesChannelId)
    }
`;

const attributesQuery = graphql`
    query composerImportAttributeDialogAttributesQuery($modelId: ID!) {
        model(id: $modelId) {
            attributes {
                id
                attributeID
                attributeGuid
                attributeCode
                attributeTypeID
                defaultValue
                displayOrder
                name
                description
                groupID
                groupGuid
                stepID
                stepGuid
                optionPreviewShapeID
                options {
                    optionID
                    optionGuid
                    optionCode
                    tempImageFileKey
                    tempImageUrl
                    imageUrl
                    tempImageFileObjectID
                    imageFileObjectID
                    name
                    description
                    displayOrder
                    isDefault
                    constraintExpression {
                        op
                        propositions {
                            attributeGuid
                            op
                            value
                        }
                    }
                    actions {
                        actionID
                        actionGuid
                        name
                        actionTypeID
                        sceneElementTypeID
                        selector
                        propertyName
                        value
                        execOrder
                        deviceTypes
                    }
                    extensionFields {
                        extensionFieldID
                        value
                        name
                        label
                        formatString
                        dataTypeID
                        operationTypeID
                    }
                }
                preActions {
                    actionID
                    actionGuid
                    name
                    actionTypeID
                    sceneElementTypeID
                    selector
                    propertyName
                    value
                    execOrder
                    deviceTypes
                }
                valueActions {
                    actionID
                    actionGuid
                    name
                    actionTypeID
                    sceneElementTypeID
                    selector
                    propertyName
                    value
                    execOrder
                    deviceTypes
                }
                postActions {
                    actionID
                    actionGuid
                    name
                    actionTypeID
                    sceneElementTypeID
                    selector
                    propertyName
                    value
                    execOrder
                    deviceTypes
                }
                meshSelector
                sceneCameraLocationID
                hideOptionsLabel
            }
        }
    }
`;

// Update your ComposerImportAttributesDialog component to use these components
const ComposerImportAttributesDialog: React.FC<ComposerImportAttributesDialogProps> = ({ salesChannelID, materialsList, objectsList, onConfirm }) => {
    const validationContext = useValidationContext();
    const environment = useRelayEnvironment();
    const [currentProduct, setCurrentProduct] = React.useState<Omit<RelayFragment<merchantProduct_item$data>, ' $fragmentType'> | null>(null);
    const [objectMap, setObjectMap] = React.useState<Record<string, string>>({});
    const [materialMap, setMaterialMap] = React.useState<Record<string, string>>({});
    const [currentStep, setCurrentStep] = React.useState<ComposerImportStep>(ComposerImportStep.SelectProduct);

    const [productAttributes, setProductAttributes] = React.useState<ComposerAttributeNode[]>([]);
    const [selectedAttributes, setSelectedAttributes] = React.useState<ComposerAttributeNode[]>([]);

    // Track completion of each step
    const [stepsCompleted, setStepsCompleted] = React.useState({
        [ComposerImportStep.SelectProduct]: false,
        [ComposerImportStep.SelectAttributes]: false,
        [ComposerImportStep.MatchObjectAndMaterials]: false
    });

    // Compute used objects and materials from selected attributes' actions
    const { usedObjects, usedMaterials } = React.useMemo(() => {
        const actions = selectedAttributes.flatMap((attr) => attr.options.flatMap((opt) => opt.actions || []));
        const { objectIds, materialIds } = getRetargetObjectsAndMaterials(actions);
        return {
            usedObjects: objectIds.map((id) => ({ id, name: id })),
            usedMaterials: materialIds.map((id) => ({ id, name: id }))
        };
    }, [selectedAttributes]);

    const sceneMeshOptions = React.useMemo(() => objectsList.map((o) => ({ label: o.name, value: o.id })), [objectsList]);
    const sceneMaterialOptions = React.useMemo(() => materialsList.map((m) => ({ label: m.name, value: m.id })), [materialsList]);

    const handleStepClick = (step: ComposerImportStep) => {
        const canAccess =
            step === ComposerImportStep.SelectProduct ||
            (step === ComposerImportStep.SelectAttributes && stepsCompleted[ComposerImportStep.SelectProduct]) ||
            (step === ComposerImportStep.MatchObjectAndMaterials && stepsCompleted[ComposerImportStep.SelectAttributes]);

        if (canAccess) {
            setCurrentStep(step);
        }
    };

    const handleConfirm = () => {
        if (!validationContext.submit()) return;

        const result: ComposerImportAttributesOutput = {
            selectedAttributes,
            objectMatches: objectMap,
            materialMatches: materialMap
        };
        onConfirm(result);
    };

    const handleProductClick = async (product: Omit<RelayFragment<merchantProduct_item$data>, ' $fragmentType'>) => {
        setCurrentProduct(product);
        if (!product.sku) return;

        const modelIdResult = await fetchQuery<composerImportAttributeDialogModelIDQuery>(
            environment,
            modelIDByCodeQuery,
            {
                code: product.sku,
                salesChannelId: salesChannelID
            },
            { fetchPolicy: 'network-only' }
        ).toPromise();

        const attributesQueryData = await fetchQuery<composerImportAttributeDialogAttributesQuery>(
            environment,
            attributesQuery,
            { modelId: modelIdResult?.modelIDByCode || '' },
            { fetchPolicy: 'network-only' }
        ).toPromise();

        const parsed = ComposerAttributeNodeAdapter.adjustValues(
            (attributesQueryData?.model?.attributes || []).map((attr) => ComposerAttributeNodeAdapter.toNode(ComposerAttributeNodeAdapter.fromJSON(attr)))
        );
        setProductAttributes(parsed);
        setSelectedAttributes([]);
        setStepsCompleted({
            [ComposerImportStep.SelectProduct]: true,
            [ComposerImportStep.SelectAttributes]: false,
            [ComposerImportStep.MatchObjectAndMaterials]: false
        });
        setObjectMap({});
        setMaterialMap({});
        setCurrentStep(ComposerImportStep.SelectAttributes);
    };

    const handleAttributeSelect = (attribute: ComposerAttributeNode) => {
        setSelectedAttributes([attribute]);
        setObjectMap({});
        setMaterialMap({});
        setStepsCompleted((prev) => ({
            ...prev,
            [ComposerImportStep.SelectAttributes]: true,
            [ComposerImportStep.MatchObjectAndMaterials]: false
        }));
        setCurrentStep(ComposerImportStep.MatchObjectAndMaterials);
    };

    const hasMatchItems = usedObjects.length > 0 || usedMaterials.length > 0;

    return (
        <Dialog title={T._('Import Attributes', 'Admin')} buttons={[{ label: T._('Confirm', 'Admin'), onClick: handleConfirm }]}>
            <Stack $gap='16px'>
                <ValidationBox
                    fullWidth
                    validationKey='composerImportAttribute'
                    validationLabel={T._('Attribute', 'Admin')}
                    validationValue={selectedAttributes}
                    validate={(value) => value.length > 0}
                    validationMessage={T._('Attribute is required.', 'Admin')}
                >
                    <StepContainer>
                        {/* Step 1 */}
                        <StepHeader
                            $isActive={currentStep === ComposerImportStep.SelectProduct}
                            $isCompleted={stepsCompleted[ComposerImportStep.SelectProduct]}
                            $isDisabled={false}
                            onClick={() => handleStepClick(ComposerImportStep.SelectProduct)}
                        >
                            <StepNumber
                                $isActive={currentStep === ComposerImportStep.SelectProduct}
                                $isCompleted={stepsCompleted[ComposerImportStep.SelectProduct]}
                            >
                                1
                            </StepNumber>
                            <StepTitle>
                                {T._('Select Product', 'Admin')}{' '}
                                {currentProduct && (
                                    <>
                                        - {currentProduct?.name} ({currentProduct?.sku})
                                    </>
                                )}
                            </StepTitle>
                        </StepHeader>
                        <StepContent isOpen={currentStep === ComposerImportStep.SelectProduct}>
                            <StepContentInner>
                                <StoreCatalog
                                    cardTitle={T._('Select the products you want to import attributes from', 'Admin')}
                                    salesChannelID={salesChannelID}
                                    onProductClick={handleProductClick}
                                    showHeader={false}
                                    hideAlreadyConfiguredBadge={true}
                                    showBackButton={false}
                                />
                            </StepContentInner>
                        </StepContent>

                        {/* Step 2 */}
                        <StepHeader
                            $isActive={currentStep === ComposerImportStep.SelectAttributes}
                            $isCompleted={stepsCompleted[ComposerImportStep.SelectAttributes]}
                            $isDisabled={!stepsCompleted[ComposerImportStep.SelectProduct]}
                            onClick={() => handleStepClick(ComposerImportStep.SelectAttributes)}
                        >
                            <StepNumber
                                $isActive={currentStep === ComposerImportStep.SelectAttributes}
                                $isCompleted={stepsCompleted[ComposerImportStep.SelectAttributes]}
                            >
                                2
                            </StepNumber>
                            <StepTitle>{T._('Select Attributes', 'Admin')}</StepTitle>
                        </StepHeader>
                        <StepContent isOpen={currentStep === ComposerImportStep.SelectAttributes}>
                            <StepContentInner>
                                <ComposerImportAttributeList>
                                    {productAttributes.map((attribute) => (
                                        <ComposerImportAttributeItem
                                            key={attribute.id}
                                            $selected={selectedAttributes.some((a) => a.id === attribute.id)}
                                            onClick={() => handleAttributeSelect(attribute)}
                                        >
                                            <span>{attribute.name}</span>
                                        </ComposerImportAttributeItem>
                                    ))}
                                </ComposerImportAttributeList>
                            </StepContentInner>
                        </StepContent>

                        {/* Step 3 */}
                        <StepHeader
                            $isActive={currentStep === ComposerImportStep.MatchObjectAndMaterials}
                            $isCompleted={stepsCompleted[ComposerImportStep.MatchObjectAndMaterials]}
                            $isDisabled={!stepsCompleted[ComposerImportStep.SelectAttributes]}
                            onClick={() => handleStepClick(ComposerImportStep.MatchObjectAndMaterials)}
                        >
                            <StepNumber
                                $isActive={currentStep === ComposerImportStep.MatchObjectAndMaterials}
                                $isCompleted={stepsCompleted[ComposerImportStep.MatchObjectAndMaterials]}
                            >
                                3
                            </StepNumber>
                            <StepTitle>{T._('Match Objects & Materials', 'Admin')}</StepTitle>
                        </StepHeader>
                        <StepContent isOpen={currentStep === ComposerImportStep.MatchObjectAndMaterials}>
                            <StepContentInner>
                                {!hasMatchItems && <div>{T._('No objects or materials to match for the selected attribute.', 'Admin')}</div>}
                                {hasMatchItems && (
                                    <p>
                                        {T._('Select the scene objects and materials that will replace those used by the imported attribute actions.', 'Admin')}
                                    </p>
                                )}
                                {usedObjects.length > 0 && (
                                    <>
                                        <h3>{T._('Objects', 'Admin')}</h3>
                                        {usedObjects.map((obj) => (
                                            <MatchFormInline key={obj.id}>
                                                <span>{obj.name}</span>
                                                <Select
                                                    options={sceneMeshOptions}
                                                    placeholder={T._('Select Object', 'Admin')}
                                                    isClearable
                                                    onChange={(sel) => {
                                                        if (!sel) {
                                                            setObjectMap((prev) => {
                                                                const copy = { ...prev };
                                                                delete copy[obj.id];
                                                                return copy;
                                                            });
                                                            return;
                                                        }
                                                        setObjectMap((prev) => ({ ...prev, [obj.id]: sel.value }));
                                                    }}
                                                />
                                            </MatchFormInline>
                                        ))}
                                    </>
                                )}
                                {usedMaterials.length > 0 && (
                                    <>
                                        <h3>{T._('Materials', 'Admin')}</h3>
                                        {usedMaterials.map((mat) => (
                                            <MatchFormInline key={mat.id}>
                                                <span>{mat.name}</span>
                                                <Select
                                                    options={sceneMaterialOptions}
                                                    placeholder={T._('Select Material', 'Admin')}
                                                    isClearable
                                                    onChange={(sel) => {
                                                        if (!sel) {
                                                            setMaterialMap((prev) => {
                                                                const copy = { ...prev };
                                                                delete copy[mat.id];
                                                                return copy;
                                                            });
                                                            return;
                                                        }
                                                        setMaterialMap((prev) => ({ ...prev, [mat.id]: sel.value }));
                                                    }}
                                                />
                                            </MatchFormInline>
                                        ))}
                                    </>
                                )}
                            </StepContentInner>
                        </StepContent>
                    </StepContainer>
                </ValidationBox>
            </Stack>
        </Dialog>
    );
};

const ComposerImportAttributesDialogWithValidation: React.FC<ComposerImportAttributesDialogProps> = (props) => (
    <ValidationStateProvider>
        <ComposerImportAttributesDialog {...props} />
    </ValidationStateProvider>
);

export default ComposerImportAttributesDialogWithValidation;
